import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import moment from 'moment';

import { useUserStrategies } from '../../../context/UserStrategies';

import { ChartLine } from '../../Strategies/ChartLine';
import { StrategyLabel } from '../../Strategies/StrategyLabel';

import {
  getDataForResponsiveLine,
  getEndDate,
  getStartEndDates,
  getSymbol,
} from '../../../helpers/functions';

import { useToggle } from '../../../hooks/useToggle';
import { t } from 'i18next';
import { INTERVALS, useIntervals } from '../../../hooks/useIntervals';
import { DatePicker } from '../../form';
import { ActionButton } from '../../UI';
import { Icon } from '../../UI';
import { Checkbox, DropdownMenu } from '../../form';
import CalendarRange from '../../CalendarRange/CalendarRange';
import { TTickerCurrency } from '../../../types/model';
import { CurrencyCodeEnum } from '../../../constants/enums';

export const StrategyBacktest = ({
  strategiesId = [],
  standAlone = false,
  width = 3,
  setLegend,
}: {
  height?: number;
  standAlone?: boolean;
  strategiesId?: string[];
  width?: number;
  setLegend: Dispatch<SetStateAction<JSX.Element | undefined>>;
}) => {
  const { strategies } = useUserStrategies();
  const [selectedStrategies, setSelectedStrategies] = useState(
    strategiesId.length > 0
      ? [strategies.find(({ id }) => id === strategiesId[0])!]
      : strategies.slice(0, 2)
  );

  const {
    toggleState: isBlackSwanToggleActive,
    ToggleComponent: BlackSwanToggle,
    handleToggleState: setBlackSwanToggleState,
  } = useToggle({ description: t('widget.blackSwan') });

  useEffect(() => {
    setLegend(
      width === 1 ? undefined : (
        <div
          className={`
            flex
            border-l
            border-grey-10
            dark:border-grey-70
            px-3
            h-full
            border-r mr-6 gap-3 divide-x divide-grey-10
            ml-auto
          `}
        >
          <BlackSwanToggle />
        </div>
      )
    );

    return () => setLegend?.(undefined);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlackSwanToggleActive, width]);

  const currency = selectedStrategies[0]?.currency ?? CurrencyCodeEnum.USD;

  const {
    interval,
    IntervalSelector,
    handleInterval: resetInterval,
  } = useIntervals(['VAR_7D', 'VAR_30D', 'VAR_1Y']);

  const [[startDate, endDate], setDates] = useState([
    new Date(moment().subtract(7, 'd').format('YYYY/MM/DD')),
    getEndDate(),
  ]);

  const handleCalendar = (newDates: [Date, Date]) => {
    // console.log({ newDates });
    setDates(newDates);
    resetInterval();
  };

  useEffect(() => {
    if (interval !== 'VAR_24H') {
      const { endDate, startDate } = getStartEndDates(interval);

      setDates([startDate, endDate]);
    }
  }, [interval]);

  const chartData = selectedStrategies.map(
    ({ createdAt, currency, color, name, strategyAssets: { items: assets } }) =>
      getDataForResponsiveLine({
        assets,
        color,
        currency: standAlone
          ? 'usd'
          : (currency.toLocaleLowerCase() as TTickerCurrency),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        name,
        startDate: moment(startDate).format('YYYY-MM-DD'),
        strategyCreatedAt: createdAt,
        usePercentage: selectedStrategies.length > 1,
        useCreationDates: false,
        useUnit: true,
      })
  );

  const handleSelectedStrategies = ({
    id: selectedId,
    add,
  }: {
    id: string;
    add: boolean;
  }) => {
    setSelectedStrategies(
      add
        ? [
            ...selectedStrategies,
            strategies.find(({ id }) => id === selectedId)!,
          ]
        : selectedStrategies.filter(({ id }) => selectedId !== id)
    );
  };

  const { dropdownItems, dropdownItemsData } = strategies
    .filter(
      ({ id }) => !selectedStrategies.map(({ id: strId }) => strId).includes(id)
    )
    .reduce(
      ({ dropdownItems, dropdownItemsData }, { id, name }) => {
        dropdownItems.push(name);
        dropdownItemsData.push(id);

        return { dropdownItems, dropdownItemsData };
      },
      { dropdownItems: [] as string[], dropdownItemsData: [] as string[] }
    );

  const isDropdownDisabled =
    selectedStrategies.length === 5 ||
    selectedStrategies.length === strategies.length;

  const [showCalendar, setShowCalendar] = useState(false);
  const [dateRangeState, setDateRangeState] = useState([
    {
      startDate,
      endDate,
      key: 'selection',
    },
  ]);

  const handleDateRangePickerChange = (item) => {
    const { selection } = item;

    setDateRangeState([selection]);
    setDates([selection.startDate, selection.endDate]);
  };

  const dropdownItemsWidth1 = [
    {
      item: interval,
      hideItemOnClick: false,
      dropdownItems: [...INTERVALS],
      handleClick: resetInterval,
    },

    {
      item: (
        <div
          className="flex items-center gap-3 w-full"
          onClick={() => setShowCalendar((pr) => !pr)}
        >
          <Icon svgName="cal" />
          <span className="mr-auto capitalize-first">
            {t('common.custom', { value: t('common.date') })}
          </span>
        </div>
      ),
      withBorder: true,
    },

    <div
      className="flex items-center gap-3 w-full"
      onClick={() => setBlackSwanToggleState()}
    >
      <span className="mr-auto">{t('widget.blackSwan')}</span>
      {isBlackSwanToggleActive && (
        <Checkbox shape="circle" isCheck={true} size="small" />
      )}
    </div>,
  ];

  return showCalendar ? (
    <CalendarRange
      goBack={() => setShowCalendar(false)}
      ranges={dateRangeState}
      startDate={startDate}
      endDate={endDate}
      handleDateRangeChange={handleDateRangePickerChange}
    />
  ) : (
    <div className="flex flex-col h-full w-full bg-white dark:bg-grey-90 ">
      <div className="flex items-center border-b border-grey-10 dark:border-grey-70">
        {!standAlone ? (
          <>
            {selectedStrategies.map(({ color, id, name }) => (
              <StrategyLabel
                key={id}
                color={color}
                name={name}
                className="h-10 my-2 ml-3"
              />
            ))}
          </>
        ) : (
          <div className={'flex items-center ml-5'}>
            <DropdownMenu
              dropdownClassName={`
                  flex
                  items-center
                  ${isDropdownDisabled && 'text-grey-30'}
                `}
              dropdownTooltipClassName={`
                  bg-white
                  border-solid
                  border
                  border-grey-30
                  dark:border-grey-70
                `}
              dropdownTooltipXOffset={-10}
              dropdownTooltipYOffset={10}
              dropdownItems={dropdownItems}
              dropdownItemsData={dropdownItemsData}
              handleClick={(name, id) =>
                handleSelectedStrategies({ id, add: true })
              }
              isDropdownDisabled={isDropdownDisabled}
              disableCursorOverride={isDropdownDisabled}
            >
              <ActionButton
                text={t('strategies.addStrategy')}
                size={'large'}
                isDisabled={isDropdownDisabled}
              />
            </DropdownMenu>

            {selectedStrategies.map(({ id, name, color }) => (
              <StrategyLabel
                className={'ml-3'}
                key={id}
                color={color}
                name={name}
                viewType="tag"
                onClose={() => handleSelectedStrategies({ id, add: false })}
              />
            ))}
          </div>
        )}
        {width !== 1 ? (
          <div
            className={
              'flex items-center ml-auto border border-grey-10 h-16 dark:border-grey-80 px-3'
            }
          >
            <DatePicker
              size={'medium'}
              isRangePicker={true}
              hasBorderBottom={false}
              id={'strategyBacktestDate'}
              startDate={startDate}
              endDate={endDate}
              onChange={(range) => handleCalendar(range as [Date, Date])}
              maxDate={new Date()}
            />
            <IntervalSelector
              className={`
              ml-4
                border-l
              border-grey-10
              dark:border-grey-70`}
            />
          </div>
        ) : (
          <DropdownMenu
            dropdownClassName={`
              flex
              items-center
              border
              h-full
              border-transparent
              cursor-pointer
              hover:border-black
              hover:border
              dark:border-grey-70
              dark:hover:border-grey-30
              ml-auto
            `}
            dropdownTooltipClassName={`
              bg-white
              pl-0
              border-solid
              border
              border-grey-30
            `}
            dropdownTooltipXOffset={-60}
            // dropdownTooltipYOffset={5}
            dropdownItems={dropdownItemsWidth1}
          >
            <Icon svgName="options" className="w-12 h-12 py-4 pr-3" />
          </DropdownMenu>
        )}
      </div>
      <div className={'w-full h-240 overflow-hidden'}>
        <ChartLine
          canShowBlackSwan={isBlackSwanToggleActive}
          data={chartData}
          height="full"
          hideAxis={width === 1}
          suffix={getSymbol(currency)}
          usePercentage={false}
          useVariation={true}
          setBlackSwanToggleState={setBlackSwanToggleState}
        />
      </div>
    </div>
  );
};
